import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppSettings } from 'pac-api';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';

import { CatchError } from './helpers';
import { LoggerService } from './logger.service';
import { IndividualAffiliationModel } from './model/individual-affiliation.model';
import { GenericErrorHandlerService } from './shared/generic-error-handler.service';

@Injectable({
  providedIn: 'root'
})
export class IndividualAffiliationService {

  private cache: IndividualAffiliationModel[];
  private Affiliations$: BehaviorSubject<IndividualAffiliationModel[]>;

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    private settings: AppSettings,
    private genericErrorHanlder: GenericErrorHandlerService,
  ) {
    this.cache = null;
    this.Affiliations$ = null;
  }

  public GetAffiliation(): Observable<IndividualAffiliationModel[]> {
    if (this.Affiliations$) {
      return this.Affiliations$;
    }

    this.Affiliations$ = new BehaviorSubject(this.cache);

    this.http.get(this.settings.pac_base_nf_url + 'api/IndividualAffiliation', {
      withCredentials: true
    }).pipe(catchError(CatchError), map(result => {
      if (result.error) {
        return result;
      } else if (result instanceof Array) {
        return result.map(item => new IndividualAffiliationModel(item));
      }
    }), tap(result => {
      try {
        this.logger.log(result);
      } catch {
      }
    }, this.genericErrorHanlder.Hanlder))
    .subscribe(result => this.Affiliations$.next(this.cache = result));

    return this.Affiliations$.pipe(filter(result => result !== null));
  }

  public DeleteAffilication(affiliation: IndividualAffiliationModel): Observable<IndividualAffiliationModel> {
    if (!affiliation || !(affiliation instanceof IndividualAffiliationModel)) {
      return null;
    }

    if (affiliation.ixo_end_date) {
      return of(affiliation);
    }

    return this.http.delete(this.settings.pac_base_nf_url + 'api/IndividualAffiliation/' + affiliation.ixo_key, {
      withCredentials: true
    }).pipe(catchError(CatchError), map(result => {
      if (result.error) {
        return result;
      } else if ('ixo_key' in result) {
        return new IndividualAffiliationModel(result);
      } else if (result instanceof Array) {
        return result.map(item => new IndividualAffiliationModel(item));
      }
    }), tap(result => {
      try {
        // Result may be a single ixo, or a new dump of all of them depending on if the
        // affilitation was part of a group or not
        if (result instanceof IndividualAffiliationModel && this.cache) {
          const i = this.cache.findIndex(ixo => ixo.ixo_key === result.ixo_key);

          if (i) {
            this.cache[i] = result;
          } else {
            this.cache.push(result);
          }
        } else if (result instanceof Array) {
          this.cache = result;
        }

        if (this.Affiliations$ && this.cache) {
          this.Affiliations$.next(this.cache);
        }

        this.logger.log(result);
      } catch {
      }
    }, this.genericErrorHanlder.Hanlder), map (result => {
      if (result instanceof Array) {
        return result.find(ixo => ixo.ixo_key === affiliation.ixo_key);
      } else {
        return result;
      }
    }));
  }
}
